import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';
import type { WxbDataTableProps } from '../components/wxb-ui/DataTable/DataTable';
import { WxbBadge } from '../components/wxb-ui/Badge/Badge';
import { WxbButton } from '../components/wxb-ui/Button/Button';
import { WxbBulkActionBar, WxbDataTable } from '../components/wxb-ui/DataTable/DataTable';
import { WxbEmpty } from '../components/wxb-ui/Empty/Empty';
import { WxbFormField } from '../components/wxb-ui/FormField/FormField';
import { WxbAlert } from '../components/wxb-ui/Alert/Alert';
import { WxbIconButton } from '../components/wxb-ui/IconButton/IconButton';
import { WxbInput } from '../components/wxb-ui/Input/Input';
import { WxbInputNumber } from '../components/wxb-ui/InputNumber/InputNumber';
import { WxbModal } from '../components/wxb-ui/Modal/WxbModal';
import {
    WxbPageHeader,
    WxbPageSection,
    WxbPageShell,
    WxbPageToolbar,
} from '../components/wxb-ui/PageLayout/PageLayout';
import { WxbSearchInput } from '../components/wxb-ui/SearchInput/SearchInput';
import { WxbSelect } from '../components/wxb-ui/Select/Select';
import { WxbSpinner } from '../components/wxb-ui/Spinner/Spinner';
import { WxbSwitch } from '../components/wxb-ui/Switch/Switch';
import { WxbTabs } from '../components/wxb-ui/Tabs/Tabs';
import { WxbTag, type WxbTagColor } from '../components/wxb-ui/Tag/Tag';
import { WxbTextarea } from '../components/wxb-ui/Textarea/Textarea';
import { WxbTooltip } from '../components/wxb-ui/Tooltip/Tooltip';
import { wxbToast } from '../components/wxb-ui/Toast/Toast';
import './OperationsPage.css';

type OperationIconName = 'plus' | 'refresh' | 'edit' | 'delete' | 'clock' | 'people' | 'shield';

interface Operation {
    id: number;
    operation_code: string;
    operation_name: string;
    standard_time: number;
    required_people?: number;
    description?: string | null;
    qualification_count?: number;
    process_stage_usage_count?: number;
    batch_plan_usage_count?: number;
    constraint_usage_count?: number;
    share_group_usage_count?: number;
    external_reference_count?: number;
    operation_type_id?: number | null;
    operation_type_code?: string | null;
    operation_type_name?: string | null;
    operation_type_color?: string | null;
}

interface OperationType {
    id: number;
    type_code: string;
    type_name: string;
    team_id: number;
    team_code: string;
    team_name: string;
    color: string;
}

interface Team {
    id: number;
    unit_code: string;
    unit_name: string;
}

interface QualifiedRequirement {
    qualification_id: number;
    qualification_name: string;
    min_level: number;
    is_mandatory: boolean;
}

interface QualifiedPersonnelQualification {
    id?: number | null;
    qualification_id: number;
    qualification_name: string;
    qualification_level: number;
}

interface QualifiedPersonnel {
    employee_id: number;
    employee_code: string;
    employee_name: string;
    department_name?: string | null;
    team_name?: string | null;
    unit_name?: string | null;
    position_name?: string | null;
    qualifications: QualifiedPersonnelQualification[];
}

interface QualifiedPersonnelPosition {
    position_number: number;
    qualified_count: number;
    requirements: QualifiedRequirement[];
    personnel: QualifiedPersonnel[];
}

interface QualifiedPersonnelDetails {
    operation_id: number;
    operation_code: string;
    operation_name: string;
    required_people: number;
    positions: QualifiedPersonnelPosition[];
}

interface QualificationOption {
    id: number;
    qualification_name: string;
}

interface EmployeeQualificationDraft {
    draftKey: string;
    id: number | null;
    qualification_id: number;
    qualification_name: string;
    qualification_level: number;
}

interface OperationFormState {
    operation_name: string;
    standard_time: number | null;
    required_people: number;
    operation_type_id: number | null;
    description: string;
}

interface OperationQualificationRequirement {
    id?: number | null;
    qualification_id: number;
    qualification_name?: string;
    min_level: number;
    is_mandatory: number | boolean;
}

interface OperationQualificationResponse {
    requiredPeople: number;
    positionRequirements?: Record<string, OperationQualificationRequirement[]>;
}

interface NewOperationRequirementDraft {
    qualification_id: number | null;
    min_level: number;
    is_mandatory: boolean;
}

type FormErrors = Partial<Record<keyof OperationFormState, string>>;

interface QualifiedModalDimensions {
    width: number;
    height: number;
}

type QualifiedModalResizeMode = 'right' | 'bottom' | 'corner';

interface QualifiedModalResizeState {
    mode: QualifiedModalResizeMode;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
}

const DEFAULT_FORM_STATE: OperationFormState = {
    operation_name: '',
    standard_time: null,
    required_people: 1,
    operation_type_id: null,
    description: '',
};
const DEFAULT_NEW_REQUIREMENT_DRAFT: NewOperationRequirementDraft = {
    qualification_id: null,
    min_level: 1,
    is_mandatory: true,
};

const ALL_KEY = 'all';
const UNASSIGNED_TYPE_KEY = 'unassigned';
const DELETE_STATUS_DELETABLE_KEY = 'deletable';
const DELETE_STATUS_BLOCKED_KEY = 'blocked';
const QUALIFICATION_LEVEL_MIN = 1;
const QUALIFICATION_LEVEL_MAX = 5;
const QUALIFIED_MODAL_DEFAULT_DIMENSIONS: QualifiedModalDimensions = { width: 1060, height: 760 };
const QUALIFIED_MODAL_MIN_DIMENSIONS: QualifiedModalDimensions = { width: 760, height: 620 };
const QUALIFIED_MODAL_MAX_DIMENSIONS: QualifiedModalDimensions = { width: 1320, height: 980 };
const QUALIFIED_TABLE_FIXED_HEIGHT = 400;
const QUALIFIED_TABLE_MIN_SCROLL_Y = 260;
const QUALIFIED_TABLE_MIN_SCROLL_X = 760;

const toFiniteNumber = (value: unknown, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const toPositiveInteger = (value: unknown, fallback = 1) => {
    const parsed = Math.floor(toFiniteNumber(value, fallback));
    return parsed >= 1 ? parsed : fallback;
};

const clampValue = (value: number, min: number, max: number) =>
    Math.min(max, Math.max(min, value));

const normalizeQualificationLevel = (value: unknown, fallback = QUALIFICATION_LEVEL_MIN) => {
    const parsed = toFiniteNumber(value, fallback);
    return Math.min(QUALIFICATION_LEVEL_MAX, Math.max(QUALIFICATION_LEVEL_MIN, Math.round(parsed)));
};

const normalizeOperation = (operation: Operation): Operation => ({
    ...operation,
    standard_time: toFiniteNumber(operation.standard_time),
    required_people: toPositiveInteger(operation.required_people, 1),
    qualification_count: toFiniteNumber(operation.qualification_count),
    process_stage_usage_count: toFiniteNumber(operation.process_stage_usage_count),
    batch_plan_usage_count: toFiniteNumber(operation.batch_plan_usage_count),
    constraint_usage_count: toFiniteNumber(operation.constraint_usage_count),
    share_group_usage_count: toFiniteNumber(operation.share_group_usage_count),
    external_reference_count: toFiniteNumber(operation.external_reference_count),
});

const getExternalReferenceDetails = (operation: Operation) => [
    { label: '工艺阶段', count: toFiniteNumber(operation.process_stage_usage_count) },
    { label: '批次计划', count: toFiniteNumber(operation.batch_plan_usage_count) },
    { label: '操作约束', count: toFiniteNumber(operation.constraint_usage_count) },
    { label: '人员共享组', count: toFiniteNumber(operation.share_group_usage_count) },
].filter((item) => item.count > 0);

const getExternalReferenceCount = (operation: Operation) => {
    const explicitTotal = toFiniteNumber(operation.external_reference_count);
    if (explicitTotal > 0) return explicitTotal;
    return getExternalReferenceDetails(operation).reduce((total, item) => total + item.count, 0);
};

const getDeleteBlockedReason = (operation: Operation) => {
    const details = getExternalReferenceDetails(operation);
    if (details.length === 0) return '此操作已被业务数据引用，不能删除';

    return `已被${details.map((item) => `${item.label} ${item.count} 处`).join('、')}引用，不能删除`;
};

const getPositionKey = (positionNumber: number) => positionNumber.toString();

const buildEmptyRequirementMap = (people: number): Record<number, OperationQualificationRequirement[]> => {
    const normalizedPeople = toPositiveInteger(people, 1);
    return Array.from({ length: normalizedPeople }, (_value, index) => index + 1)
        .reduce<Record<number, OperationQualificationRequirement[]>>((accumulator, position) => {
            accumulator[position] = [];
            return accumulator;
        }, {});
};

const normalizeRequirementMandatory = (value: unknown) =>
    value === true || value === 1 || value === '1';

const normalizeRequirement = (requirement: OperationQualificationRequirement): OperationQualificationRequirement | null => {
    const qualificationId = Number(requirement.qualification_id);
    if (!Number.isFinite(qualificationId)) return null;

    return {
        id: requirement.id ?? null,
        qualification_id: qualificationId,
        qualification_name: requirement.qualification_name || '',
        min_level: normalizeQualificationLevel(requirement.min_level),
        is_mandatory: normalizeRequirementMandatory(requirement.is_mandatory) ? 1 : 0,
    };
};

const normalizeRequirementMap = (
    people: number,
    requirements: OperationQualificationResponse['positionRequirements'],
) => {
    const normalizedPeople = toPositiveInteger(people, 1);
    const normalizedRequirements = buildEmptyRequirementMap(normalizedPeople);

    Object.entries(requirements || {}).forEach(([positionKey, positionRequirements]) => {
        const position = Number(positionKey);
        if (!Number.isInteger(position) || position < 1 || !Array.isArray(positionRequirements)) return;

        normalizedRequirements[position] = positionRequirements
            .map(normalizeRequirement)
            .filter((requirement): requirement is OperationQualificationRequirement => requirement !== null);
    });

    return normalizedRequirements;
};

const getRequirementLabel = (requirement: QualifiedRequirement) =>
    `${requirement.qualification_name} ≥${toFiniteNumber(requirement.min_level, 1)}级${requirement.is_mandatory ? '' : ' · 可选'}`;

const isActivationKey = (event: React.KeyboardEvent) => event.key === 'Enter' || event.key === ' ';

const getQualificationLevelColor = (level: number): WxbTagColor => {
    const normalizedLevel = normalizeQualificationLevel(level);
    if (normalizedLevel >= 5) return 'red';
    if (normalizedLevel === 4) return 'amber';
    if (normalizedLevel === 3) return 'blue';
    if (normalizedLevel === 2) return 'green';
    return 'neutral';
};

const sortRequirementsByLevel = (requirements: QualifiedRequirement[]) =>
    [...requirements].sort((left, right) => {
        const levelDiff = normalizeQualificationLevel(right.min_level) - normalizeQualificationLevel(left.min_level);
        if (levelDiff !== 0) return levelDiff;

        const mandatoryDiff = Number(right.is_mandatory) - Number(left.is_mandatory);
        if (mandatoryDiff !== 0) return mandatoryDiff;

        return left.qualification_name.localeCompare(right.qualification_name, 'zh-Hans-CN');
    });

const sortPersonnelQualifications = <T extends { qualification_name: string; qualification_level: number }>(qualifications: T[]) =>
    [...qualifications].sort((left, right) => {
        const levelDiff = normalizeQualificationLevel(right.qualification_level) - normalizeQualificationLevel(left.qualification_level);
        if (levelDiff !== 0) return levelDiff;
        return left.qualification_name.localeCompare(right.qualification_name, 'zh-Hans-CN');
    });

const getPreferredPositionKey = (details: QualifiedPersonnelDetails, preferredKey?: string | null) => {
    const positions = details.positions || [];
    if (preferredKey && positions.some((position) => getPositionKey(position.position_number) === preferredKey)) {
        return preferredKey;
    }

    const firstPopulatedPosition = positions.find((position) => position.qualified_count > 0);
    return getPositionKey(firstPopulatedPosition?.position_number ?? positions[0]?.position_number ?? 1);
};

const validateForm = (formState: OperationFormState): FormErrors => {
    const nextErrors: FormErrors = {};

    if (!formState.operation_name.trim()) {
        nextErrors.operation_name = '请输入操作名称';
    }

    if (!formState.standard_time || formState.standard_time <= 0) {
        nextErrors.standard_time = '标准耗时必须大于 0';
    }

    if (
        !Number.isFinite(formState.required_people)
        || !Number.isInteger(Number(formState.required_people))
        || formState.required_people < 1
    ) {
        nextErrors.required_people = '所需人数必须为正整数';
    }

    return nextErrors;
};

const OperationIcon: React.FC<{ name: OperationIconName }> = ({ name }) => {
    const paths: Record<OperationIconName, React.ReactNode> = {
        plus: (
            <>
                <path d="M12 5v14" />
                <path d="M5 12h14" />
            </>
        ),
        refresh: (
            <>
                <path d="M20 12a8 8 0 0 1-13.5 5.8" />
                <path d="M4 12A8 8 0 0 1 17.5 6.2" />
                <path d="M17 3v4h-4" />
                <path d="M7 21v-4h4" />
            </>
        ),
        edit: (
            <>
                <path d="M4 20h16" />
                <path d="M14.5 5.5 18 9 9 18H5.5v-3.5l9-9Z" />
            </>
        ),
        delete: (
            <>
                <path d="M5 7h14" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M8 7l1 12h6l1-12" />
                <path d="M10 7V5h4v2" />
            </>
        ),
        clock: (
            <>
                <circle cx="12" cy="12" r="8" />
                <path d="M12 8v5l3 2" />
            </>
        ),
        people: (
            <>
                <path d="M16 19v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 17.5V19" />
                <circle cx="10" cy="8" r="3" />
                <path d="M20 19v-1.2a3 3 0 0 0-2.2-2.9" />
                <path d="M15.5 5.2a3 3 0 0 1 0 5.6" />
            </>
        ),
        shield: (
            <>
                <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3Z" />
                <path d="M9 12l2 2 4-4" />
            </>
        ),
    };

    return (
        <svg className="operations-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            {paths[name]}
        </svg>
    );
};

const OperationsPage: React.FC = () => {
    const [operations, setOperations] = useState<Operation[]>([]);
    const [operationTypes, setOperationTypes] = useState<OperationType[]>([]);
    const [teams, setTeams] = useState<Team[]>([]);
    const [qualifiedPersonnelMap, setQualifiedPersonnelMap] = useState<Record<number, number[]>>({});
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [activeTeamId, setActiveTeamId] = useState(ALL_KEY);
    const [searchText, setSearchText] = useState('');
    const [peopleFilter, setPeopleFilter] = useState(ALL_KEY);
    const [typeFilter, setTypeFilter] = useState(ALL_KEY);
    const [deleteStatusFilter, setDeleteStatusFilter] = useState(ALL_KEY);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingOperation, setEditingOperation] = useState<Operation | null>(null);
    const [formState, setFormState] = useState<OperationFormState>(DEFAULT_FORM_STATE);
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [nextCode, setNextCode] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [deleteCandidate, setDeleteCandidate] = useState<Operation | null>(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [bulkDeleteSubmitting, setBulkDeleteSubmitting] = useState(false);
    const [selectedOperationKeys, setSelectedOperationKeys] = useState<React.Key[]>([]);
    const [operationPage, setOperationPage] = useState(1);
    const [operationPageSize, setOperationPageSize] = useState(20);
    const [operationQualificationsLoading, setOperationQualificationsLoading] = useState(false);
    const [operationQualificationsError, setOperationQualificationsError] = useState(false);
    const [operationQualificationOptions, setOperationQualificationOptions] = useState<QualificationOption[]>([]);
    const [operationPositionRequirements, setOperationPositionRequirements] = useState<Record<number, OperationQualificationRequirement[]>>(buildEmptyRequirementMap(1));
    const [activeOperationPosition, setActiveOperationPosition] = useState('1');
    const [newOperationRequirement, setNewOperationRequirement] = useState<NewOperationRequirementDraft>(DEFAULT_NEW_REQUIREMENT_DRAFT);
    const [qualifiedDetailsOperation, setQualifiedDetailsOperation] = useState<Operation | null>(null);
    const [qualifiedDetails, setQualifiedDetails] = useState<QualifiedPersonnelDetails | null>(null);
    const [qualifiedDetailsLoading, setQualifiedDetailsLoading] = useState(false);
    const [qualifiedDetailsError, setQualifiedDetailsError] = useState(false);
    const [qualifiedDetailsActivePosition, setQualifiedDetailsActivePosition] = useState('1');
    const [qualificationOptions, setQualificationOptions] = useState<QualificationOption[]>([]);
    const [qualifiedModalDimensions, setQualifiedModalDimensions] = useState<QualifiedModalDimensions>(
        QUALIFIED_MODAL_DEFAULT_DIMENSIONS,
    );
    const [qualifiedModalResizing, setQualifiedModalResizing] = useState(false);
    const [editingPersonnelId, setEditingPersonnelId] = useState<number | null>(null);
    const [qualificationDrafts, setQualificationDrafts] = useState<Record<number, EmployeeQualificationDraft[]>>({});
    const [draftAddQualificationId, setDraftAddQualificationId] = useState<string | null>(null);
    const [draftAddLevel, setDraftAddLevel] = useState(3);
    const [savingQualificationEmployeeId, setSavingQualificationEmployeeId] = useState<number | null>(null);
    const qualifiedModalResizeRef = useRef<QualifiedModalResizeState | null>(null);
    const operationQualificationLoadRef = useRef(0);

    const operationTypeById = useMemo(() => {
        const map = new Map<number, OperationType>();
        operationTypes.forEach((type) => map.set(type.id, type));
        return map;
    }, [operationTypes]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setLoadError(false);

        try {
            const [operationsRes, typesRes, teamsRes, qualifiedRes] = await Promise.all([
                api.get<Operation[]>('/operations'),
                api.get<OperationType[]>('/operation-types'),
                api.get<Team[]>('/organization/teams'),
                api.get<Record<number, number[]>>('/operations/qualified-personnel'),
            ]);

            setOperations((operationsRes.data || []).map(normalizeOperation));
            setOperationTypes(typesRes.data || []);
            setTeams(teamsRes.data || []);
            setQualifiedPersonnelMap(qualifiedRes.data || {});
        } catch {
            setLoadError(true);
            wxbToast.error('加载操作管理数据失败');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const tabItems = useMemo(() => [
        { key: ALL_KEY, label: `全部 (${operations.length})` },
        ...teams.map((team) => {
            const count = operations.filter((operation) =>
                operation.operation_type_id
                && operationTypeById.get(operation.operation_type_id)?.team_id === team.id
            ).length;
            return { key: team.id.toString(), label: `${team.unit_name} (${count})` };
        }),
    ], [operationTypeById, operations, teams]);

    const peopleOptions = useMemo(() => {
        const peopleValues = Array.from(
            new Set(operations.map((operation) => toFiniteNumber(operation.required_people, 1))),
        ).sort((left, right) => left - right);

        return [
            { value: ALL_KEY, label: '全部人数' },
            ...peopleValues.map((value) => ({ value: value.toString(), label: `${value} 人` })),
        ];
    }, [operations]);

    const typeOptions = useMemo(() => [
        { value: ALL_KEY, label: '全部类型' },
        { value: UNASSIGNED_TYPE_KEY, label: '未分配类型' },
        ...operationTypes.map((type) => ({
            value: type.id.toString(),
            label: `${type.type_name} (${type.team_code})`,
        })),
    ], [operationTypes]);

    const deleteStatusOptions = useMemo(() => [
        { value: ALL_KEY, label: '全部删除状态' },
        { value: DELETE_STATUS_DELETABLE_KEY, label: '可删除' },
        { value: DELETE_STATUS_BLOCKED_KEY, label: '不可删除' },
    ], []);

    const formTypeOptions = useMemo(() => [
        { value: 'none', label: '不关联操作类型' },
        ...operationTypes.map((type) => ({
            value: type.id.toString(),
            label: `${type.type_name} (${type.team_code})`,
        })),
    ], [operationTypes]);

    const operationRequirementSelectOptions = useMemo(
        () => operationQualificationOptions.map((qualification) => ({
            value: qualification.id.toString(),
            label: qualification.qualification_name,
        })),
        [operationQualificationOptions],
    );

    const currentRequiredPeople = useMemo(
        () => toPositiveInteger(formState.required_people, 1),
        [formState.required_people],
    );

    const activeOperationPositionNumber = useMemo(() => {
        const parsed = Number(activeOperationPosition);
        if (!Number.isInteger(parsed)) return 1;
        return clampValue(parsed, 1, currentRequiredPeople);
    }, [activeOperationPosition, currentRequiredPeople]);

    const visibleRequirementCount = useMemo(
        () => Array.from({ length: currentRequiredPeople }, (_value, index) => index + 1)
            .reduce((total, position) => total + (operationPositionRequirements[position] || []).length, 0),
        [currentRequiredPeople, operationPositionRequirements],
    );

    const hiddenRemovedRequirementCount = useMemo(
        () => Object.entries(operationPositionRequirements)
            .filter(([position]) => Number(position) > currentRequiredPeople)
            .reduce((total, [, requirements]) => total + requirements.length, 0),
        [currentRequiredPeople, operationPositionRequirements],
    );

    const filteredOperations = useMemo(() => {
        const normalizedSearchText = searchText.trim().toLowerCase();

        return operations.filter((operation) => {
            const operationType = operation.operation_type_id
                ? operationTypeById.get(operation.operation_type_id)
                : undefined;

            const matchesSearch = !normalizedSearchText
                || operation.operation_code.toLowerCase().includes(normalizedSearchText)
                || operation.operation_name.toLowerCase().includes(normalizedSearchText)
                || (operation.operation_type_name || '').toLowerCase().includes(normalizedSearchText);

            const matchesTeam = activeTeamId === ALL_KEY
                || (operationType && operationType.team_id === Number(activeTeamId));

            const matchesPeople = peopleFilter === ALL_KEY
                || toFiniteNumber(operation.required_people, 1) === Number(peopleFilter);

            const matchesType = typeFilter === ALL_KEY
                || (typeFilter === UNASSIGNED_TYPE_KEY && !operation.operation_type_id)
                || operation.operation_type_id === Number(typeFilter);

            const externalReferenceCount = getExternalReferenceCount(operation);
            const matchesDeleteStatus = deleteStatusFilter === ALL_KEY
                || (deleteStatusFilter === DELETE_STATUS_DELETABLE_KEY && externalReferenceCount === 0)
                || (deleteStatusFilter === DELETE_STATUS_BLOCKED_KEY && externalReferenceCount > 0);

            return matchesSearch && matchesTeam && matchesPeople && matchesType && matchesDeleteStatus;
        });
    }, [activeTeamId, deleteStatusFilter, operationTypeById, operations, peopleFilter, searchText, typeFilter]);

    useEffect(() => {
        setOperationPage(1);
    }, [activeTeamId, deleteStatusFilter, peopleFilter, searchText, typeFilter]);

    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(filteredOperations.length / operationPageSize));
        setOperationPage((currentPage) => Math.min(currentPage, maxPage));
    }, [filteredOperations.length, operationPageSize]);

    const operationById = useMemo(
        () => new Map(operations.map((operation) => [operation.id, operation])),
        [operations],
    );

    const selectedOperations = useMemo(
        () => selectedOperationKeys
            .map((key) => operationById.get(Number(key)))
            .filter((operation): operation is Operation => Boolean(operation)),
        [operationById, selectedOperationKeys],
    );

    const selectedDeletableOperations = useMemo(
        () => selectedOperations.filter((operation) => getExternalReferenceCount(operation) === 0),
        [selectedOperations],
    );

    const selectedBlockedCount = selectedOperations.length - selectedDeletableOperations.length;

    useEffect(() => {
        const operationIds = new Set(operations.map((operation) => operation.id));
        setSelectedOperationKeys((currentKeys) => {
            const nextKeys = currentKeys.filter((key) => operationIds.has(Number(key)));
            return nextKeys.length === currentKeys.length ? currentKeys : nextKeys;
        });
    }, [operations]);

    const updateFormField = useCallback(
        <K extends keyof OperationFormState>(field: K, value: OperationFormState[K]) => {
            setFormState((current) => ({ ...current, [field]: value }));
            setFormErrors((current) => {
                if (!current[field]) return current;
                const nextErrors = { ...current };
                delete nextErrors[field];
                return nextErrors;
            });
        },
        [],
    );

    const fetchNextCode = useCallback(async () => {
        try {
            const response = await api.get<{ nextCode: string }>('/operations/next-code');
            setNextCode(response.data.nextCode);
        } catch {
            setNextCode('');
        }
    }, []);

    const loadOperationQualificationEditor = useCallback(async (operation: Operation) => {
        const requestId = operationQualificationLoadRef.current + 1;
        operationQualificationLoadRef.current = requestId;
        setOperationQualificationsLoading(true);
        setOperationQualificationsError(false);
        setOperationQualificationOptions([]);
        setOperationPositionRequirements(buildEmptyRequirementMap(toPositiveInteger(operation.required_people, 1)));
        setActiveOperationPosition('1');
        setNewOperationRequirement(DEFAULT_NEW_REQUIREMENT_DRAFT);

        try {
            const [requirementsResponse, optionsResponse] = await Promise.all([
                api.get<OperationQualificationResponse>(`/operation-qualifications/${operation.id}`),
                api.get<QualificationOption[]>('/operation-qualifications/available'),
            ]);

            if (operationQualificationLoadRef.current !== requestId) return;

            const responsePeople = toPositiveInteger(
                requirementsResponse.data.requiredPeople,
                toPositiveInteger(operation.required_people, 1),
            );
            const options = (optionsResponse.data || [])
                .map((qualification) => ({
                    id: Number(qualification.id),
                    qualification_name: qualification.qualification_name,
                }))
                .filter((qualification) => Number.isFinite(qualification.id) && qualification.qualification_name)
                .sort((left, right) => left.qualification_name.localeCompare(right.qualification_name, 'zh-Hans-CN'));

            setOperationQualificationOptions(options);
            setOperationPositionRequirements(normalizeRequirementMap(responsePeople, requirementsResponse.data.positionRequirements));
        } catch {
            if (operationQualificationLoadRef.current === requestId) {
                setOperationQualificationsError(true);
                setOperationPositionRequirements(buildEmptyRequirementMap(toPositiveInteger(operation.required_people, 1)));
                wxbToast.error('加载操作资质要求失败');
            }
        } finally {
            if (operationQualificationLoadRef.current === requestId) {
                setOperationQualificationsLoading(false);
            }
        }
    }, []);

    const openCreateModal = useCallback(() => {
        operationQualificationLoadRef.current += 1;
        setEditingOperation(null);
        setFormState(DEFAULT_FORM_STATE);
        setFormErrors({});
        setOperationQualificationsError(false);
        setOperationQualificationsLoading(false);
        setOperationQualificationOptions([]);
        setOperationPositionRequirements(buildEmptyRequirementMap(1));
        setActiveOperationPosition('1');
        setNewOperationRequirement(DEFAULT_NEW_REQUIREMENT_DRAFT);
        setModalVisible(true);
        fetchNextCode();
    }, [fetchNextCode]);

    const openEditModal = useCallback((operation: Operation) => {
        setEditingOperation(operation);
        setNextCode('');
        setFormState({
            operation_name: operation.operation_name,
            standard_time: toFiniteNumber(operation.standard_time),
            required_people: toFiniteNumber(operation.required_people, 1),
            operation_type_id: operation.operation_type_id ?? null,
            description: operation.description || '',
        });
        setFormErrors({});
        setModalVisible(true);
        loadOperationQualificationEditor(operation);
    }, [loadOperationQualificationEditor]);

    const closeModal = useCallback(() => {
        if (submitting) return;
        operationQualificationLoadRef.current += 1;
        setModalVisible(false);
        setEditingOperation(null);
        setFormErrors({});
        setOperationQualificationsLoading(false);
        setNewOperationRequirement(DEFAULT_NEW_REQUIREMENT_DRAFT);
    }, [submitting]);

    useEffect(() => {
        if (activeOperationPositionNumber.toString() !== activeOperationPosition) {
            setActiveOperationPosition(activeOperationPositionNumber.toString());
        }
    }, [activeOperationPosition, activeOperationPositionNumber]);

    useEffect(() => {
        setNewOperationRequirement(DEFAULT_NEW_REQUIREMENT_DRAFT);
    }, [activeOperationPosition]);

    const loadQualifiedPersonnelDetails = useCallback(async (
        operation: Operation,
        preferredPositionKey?: string | null,
    ) => {
        const [detailsResponse, qualificationsResponse] = await Promise.all([
            api.get<QualifiedPersonnelDetails>(`/operations/${operation.id}/qualified-personnel-details`),
            api.get<QualificationOption[]>('/qualifications'),
        ]);
        const details = detailsResponse.data;
        const options = (qualificationsResponse.data || [])
            .map((qualification) => ({
                id: Number(qualification.id),
                qualification_name: qualification.qualification_name,
            }))
            .filter((qualification) => Number.isFinite(qualification.id) && qualification.qualification_name)
            .sort((left, right) => left.qualification_name.localeCompare(right.qualification_name, 'zh-Hans-CN'));

        setQualifiedDetails(details);
        setQualificationOptions(options);
        setQualifiedDetailsActivePosition(getPreferredPositionKey(details, preferredPositionKey));
    }, []);

    const openQualifiedPersonnelDetails = useCallback(async (operation: Operation) => {
        setQualifiedDetailsOperation(operation);
        setQualifiedDetails(null);
        setQualifiedDetailsError(false);
        setQualifiedDetailsLoading(true);
        setQualifiedDetailsActivePosition('1');
        setEditingPersonnelId(null);
        setQualificationDrafts({});
        setDraftAddQualificationId(null);

        try {
            await loadQualifiedPersonnelDetails(operation);
        } catch {
            setQualifiedDetailsError(true);
            wxbToast.error('加载合格人员明细失败');
        } finally {
            setQualifiedDetailsLoading(false);
        }
    }, [loadQualifiedPersonnelDetails]);

    const closeQualifiedPersonnelDetails = useCallback(() => {
        setQualifiedDetailsOperation(null);
        setQualifiedDetails(null);
        setQualifiedDetailsError(false);
        setQualifiedDetailsLoading(false);
        setQualifiedDetailsActivePosition('1');
        setQualificationOptions([]);
        setEditingPersonnelId(null);
        setQualificationDrafts({});
        setDraftAddQualificationId(null);
        setSavingQualificationEmployeeId(null);
    }, []);

    const getQualifiedModalBounds = useCallback(() => {
        if (typeof window === 'undefined') {
            return {
                maxWidth: QUALIFIED_MODAL_MAX_DIMENSIONS.width,
                maxHeight: QUALIFIED_MODAL_MAX_DIMENSIONS.height,
            };
        }

        return {
            maxWidth: Math.max(
                QUALIFIED_MODAL_MIN_DIMENSIONS.width,
                Math.min(QUALIFIED_MODAL_MAX_DIMENSIONS.width, window.innerWidth - 64),
            ),
            maxHeight: Math.max(
                QUALIFIED_MODAL_MIN_DIMENSIONS.height,
                Math.min(QUALIFIED_MODAL_MAX_DIMENSIONS.height, window.innerHeight - 80),
            ),
        };
    }, []);

    const clampQualifiedModalDimensions = useCallback((dimensions: QualifiedModalDimensions) => {
        const { maxWidth, maxHeight } = getQualifiedModalBounds();

        return {
            width: clampValue(dimensions.width, QUALIFIED_MODAL_MIN_DIMENSIONS.width, maxWidth),
            height: clampValue(dimensions.height, QUALIFIED_MODAL_MIN_DIMENSIONS.height, maxHeight),
        };
    }, [getQualifiedModalBounds]);

    useEffect(() => {
        if (!qualifiedDetailsOperation) return;
        setQualifiedModalDimensions((current) => clampQualifiedModalDimensions(current));
    }, [clampQualifiedModalDimensions, qualifiedDetailsOperation]);

    useEffect(() => {
        if (!qualifiedModalResizing) return undefined;

        document.body.classList.add('operations-qualified-modal-is-resizing');

        const handlePointerMove = (event: PointerEvent) => {
            const resizeState = qualifiedModalResizeRef.current;
            if (!resizeState) return;

            const deltaX = event.clientX - resizeState.startX;
            const deltaY = event.clientY - resizeState.startY;

            setQualifiedModalDimensions(clampQualifiedModalDimensions({
                width: resizeState.mode === 'right' || resizeState.mode === 'corner'
                    ? resizeState.startWidth + deltaX
                    : resizeState.startWidth,
                height: resizeState.mode === 'bottom' || resizeState.mode === 'corner'
                    ? resizeState.startHeight + deltaY
                    : resizeState.startHeight,
            }));
        };

        const handlePointerUp = () => {
            qualifiedModalResizeRef.current = null;
            setQualifiedModalResizing(false);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp, { once: true });

        return () => {
            document.body.classList.remove('operations-qualified-modal-is-resizing');
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
    }, [clampQualifiedModalDimensions, qualifiedModalResizing]);

    const beginQualifiedModalResize = useCallback((
        event: React.PointerEvent<HTMLElement>,
        mode: QualifiedModalResizeMode,
    ) => {
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        qualifiedModalResizeRef.current = {
            mode,
            startX: event.clientX,
            startY: event.clientY,
            startWidth: qualifiedModalDimensions.width,
            startHeight: qualifiedModalDimensions.height,
        };
        setQualifiedModalResizing(true);
    }, [qualifiedModalDimensions]);

    const handleQualifiedModalResizeKeyDown = useCallback((
        event: React.KeyboardEvent<HTMLElement>,
        mode: QualifiedModalResizeMode,
    ) => {
        const step = event.shiftKey ? 40 : 20;
        let widthDelta = 0;
        let heightDelta = 0;

        if ((mode === 'right' || mode === 'corner') && event.key === 'ArrowRight') widthDelta = step;
        if ((mode === 'right' || mode === 'corner') && event.key === 'ArrowLeft') widthDelta = -step;
        if ((mode === 'bottom' || mode === 'corner') && event.key === 'ArrowDown') heightDelta = step;
        if ((mode === 'bottom' || mode === 'corner') && event.key === 'ArrowUp') heightDelta = -step;

        if (widthDelta === 0 && heightDelta === 0) return;

        event.preventDefault();
        setQualifiedModalDimensions((current) => clampQualifiedModalDimensions({
            width: current.width + widthDelta,
            height: current.height + heightDelta,
        }));
    }, [clampQualifiedModalDimensions]);

    const qualificationSelectOptions = useMemo(
        () => qualificationOptions.map((qualification) => ({
            value: qualification.id.toString(),
            label: qualification.qualification_name,
        })),
        [qualificationOptions],
    );

    const buildPersonnelQualificationDrafts = useCallback((personnel: QualifiedPersonnel) =>
        sortPersonnelQualifications(personnel.qualifications).map((qualification) => ({
            draftKey: qualification.id
                ? `existing-${qualification.id}`
                : `existing-${personnel.employee_id}-${qualification.qualification_id}`,
            id: qualification.id ?? null,
            qualification_id: qualification.qualification_id,
            qualification_name: qualification.qualification_name,
            qualification_level: normalizeQualificationLevel(qualification.qualification_level),
        })), []);

    const beginEditPersonnelQualifications = useCallback((personnel: QualifiedPersonnel) => {
        if (editingPersonnelId === personnel.employee_id) {
            setDraftAddQualificationId(null);
            setEditingPersonnelId(null);
            return;
        }

        setQualificationDrafts((drafts) => ({
            ...drafts,
            [personnel.employee_id]: buildPersonnelQualificationDrafts(personnel),
        }));
        setDraftAddQualificationId(null);
        setDraftAddLevel(3);
        setEditingPersonnelId(personnel.employee_id);
    }, [buildPersonnelQualificationDrafts, editingPersonnelId]);

    const cancelEditPersonnelQualifications = useCallback(() => {
        setEditingPersonnelId(null);
        setDraftAddQualificationId(null);
    }, []);

    const updateQualificationDraft = useCallback((
        employeeId: number,
        draftKey: string,
        patch: Partial<EmployeeQualificationDraft>,
    ) => {
        setQualificationDrafts((current) => ({
            ...current,
            [employeeId]: (current[employeeId] || []).map((draft) =>
                draft.draftKey === draftKey ? { ...draft, ...patch } : draft,
            ),
        }));
    }, []);

    const removeQualificationDraft = useCallback((employeeId: number, draftKey: string) => {
        setQualificationDrafts((current) => ({
            ...current,
            [employeeId]: (current[employeeId] || []).filter((draft) => draft.draftKey !== draftKey),
        }));
    }, []);

    const addQualificationDraft = useCallback((personnel: QualifiedPersonnel) => {
        const qualificationId = Number(draftAddQualificationId);
        const option = qualificationOptions.find((qualification) => qualification.id === qualificationId);

        if (!Number.isFinite(qualificationId) || !option) {
            wxbToast.warning('请选择要添加的资质');
            return;
        }

        const currentDrafts = qualificationDrafts[personnel.employee_id] || [];
        if (currentDrafts.some((draft) => draft.qualification_id === qualificationId)) {
            wxbToast.warning('该人员已包含此资质');
            return;
        }

        setQualificationDrafts((current) => {
            const nextDrafts = sortPersonnelQualifications([
                ...(current[personnel.employee_id] || []),
                {
                    draftKey: `new-${personnel.employee_id}-${qualificationId}-${Date.now()}`,
                    id: null,
                    qualification_id: qualificationId,
                    qualification_name: option.qualification_name,
                    qualification_level: normalizeQualificationLevel(draftAddLevel, 3),
                },
            ]);

            return {
                ...current,
                [personnel.employee_id]: nextDrafts,
            };
        });
        setDraftAddQualificationId(null);
        setDraftAddLevel(3);
    }, [draftAddLevel, draftAddQualificationId, qualificationDrafts, qualificationOptions]);

    const savePersonnelQualifications = useCallback(async (personnel: QualifiedPersonnel) => {
        const drafts = qualificationDrafts[personnel.employee_id] || [];
        const seenQualificationIds = new Set<number>();

        for (const draft of drafts) {
            if (!Number.isFinite(draft.qualification_id)) {
                wxbToast.warning('请先补全资质');
                return;
            }

            if (seenQualificationIds.has(draft.qualification_id)) {
                wxbToast.warning('同一人员不能重复配置相同资质');
                return;
            }

            seenQualificationIds.add(draft.qualification_id);
        }

        const originalByRecordId = new Map<number, QualifiedPersonnelQualification>();
        personnel.qualifications.forEach((qualification) => {
            if (qualification.id !== null && qualification.id !== undefined) {
                originalByRecordId.set(Number(qualification.id), qualification);
            }
        });

        const draftRecordIds = new Set<number>();
        const requests: Promise<unknown>[] = [];

        drafts.forEach((draft) => {
            const payload = {
                employee_id: personnel.employee_id,
                qualification_id: draft.qualification_id,
                qualification_level: normalizeQualificationLevel(draft.qualification_level),
            };

            if (draft.id !== null && draft.id !== undefined) {
                const recordId = Number(draft.id);
                draftRecordIds.add(recordId);
                const original = originalByRecordId.get(recordId);
                const changed = !original
                    || original.qualification_id !== payload.qualification_id
                    || normalizeQualificationLevel(original.qualification_level) !== payload.qualification_level;

                if (changed) {
                    requests.push(api.put(`/employee-qualifications/${recordId}`, payload));
                }
                return;
            }

            requests.push(api.post('/employee-qualifications', payload));
        });

        originalByRecordId.forEach((_qualification, recordId) => {
            if (!draftRecordIds.has(recordId)) {
                requests.push(api.delete(`/employee-qualifications/${recordId}`));
            }
        });

        if (requests.length === 0) {
            wxbToast.info('资质未发生变化');
            setEditingPersonnelId(null);
            return;
        }

        setSavingQualificationEmployeeId(personnel.employee_id);
        try {
            await Promise.all(requests);
            wxbToast.success('人员资质已更新');
            setEditingPersonnelId(null);
            setDraftAddQualificationId(null);

            if (qualifiedDetailsOperation) {
                await loadQualifiedPersonnelDetails(qualifiedDetailsOperation, qualifiedDetailsActivePosition);
            }
            await fetchData();
        } catch (error: any) {
            wxbToast.error(error?.response?.data?.error || '保存人员资质失败');
        } finally {
            setSavingQualificationEmployeeId(null);
        }
    }, [
        fetchData,
        loadQualifiedPersonnelDetails,
        qualificationDrafts,
        qualifiedDetailsActivePosition,
        qualifiedDetailsOperation,
    ]);

    const renderPersonnelQualificationEditor = useCallback((personnel: QualifiedPersonnel) => {
        const drafts = qualificationDrafts[personnel.employee_id] || [];
        const selectedQualificationIds = new Set(drafts.map((draft) => draft.qualification_id));
        const addOptions = qualificationSelectOptions.map((option) => ({
            ...option,
            disabled: selectedQualificationIds.has(Number(option.value)),
        }));
        const saving = savingQualificationEmployeeId === personnel.employee_id;

        return (
            <div className="operations-qualification-editor">
                <div className="operations-qualification-editor-head">
                    <span className="operations-qualification-editor-title">{personnel.employee_name} 的资质</span>
                    <span className="operations-muted">{personnel.employee_code}</span>
                </div>

                <div className="operations-qualification-editor-list">
                    {drafts.length > 0 ? drafts.map((draft) => {
                        const options = qualificationSelectOptions.map((option) => ({
                            ...option,
                            disabled: selectedQualificationIds.has(Number(option.value))
                                && Number(option.value) !== draft.qualification_id,
                        }));

                        return (
                            <div key={draft.draftKey} className="operations-qualification-editor-row">
                                <WxbSelect
                                    value={draft.qualification_id.toString()}
                                    options={options}
                                    showSearch
                                    optionFilterProp="label"
                                    className="operations-qualification-editor-select"
                                    onChange={(value) => {
                                        const qualificationId = Number(value);
                                        const option = qualificationOptions.find((qualification) => qualification.id === qualificationId);
                                        updateQualificationDraft(personnel.employee_id, draft.draftKey, {
                                            qualification_id: qualificationId,
                                            qualification_name: option?.qualification_name || draft.qualification_name,
                                        });
                                    }}
                                />
                                <WxbInputNumber
                                    min={QUALIFICATION_LEVEL_MIN}
                                    max={QUALIFICATION_LEVEL_MAX}
                                    precision={0}
                                    value={draft.qualification_level}
                                    className="operations-qualification-editor-level"
                                    onChange={(value) => updateQualificationDraft(personnel.employee_id, draft.draftKey, {
                                        qualification_level: normalizeQualificationLevel(value, draft.qualification_level),
                                    })}
                                />
                                <WxbTag color={getQualificationLevelColor(draft.qualification_level)}>
                                    L{normalizeQualificationLevel(draft.qualification_level)}
                                </WxbTag>
                                <WxbButton
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeQualificationDraft(personnel.employee_id, draft.draftKey)}
                                >
                                    移除
                                </WxbButton>
                            </div>
                        );
                    }) : (
                        <span className="operations-muted">暂无资质</span>
                    )}
                </div>

                <div className="operations-qualification-editor-add">
                    <WxbSelect
                        value={draftAddQualificationId ?? undefined}
                        placeholder="添加资质"
                        options={addOptions}
                        showSearch
                        optionFilterProp="label"
                        className="operations-qualification-editor-add-select"
                        onChange={(value) => setDraftAddQualificationId(String(value))}
                    />
                    <WxbInputNumber
                        min={QUALIFICATION_LEVEL_MIN}
                        max={QUALIFICATION_LEVEL_MAX}
                        precision={0}
                        value={draftAddLevel}
                        className="operations-qualification-editor-level"
                        onChange={(value) => setDraftAddLevel(normalizeQualificationLevel(value, 3))}
                    />
                    <WxbButton
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => addQualificationDraft(personnel)}
                    >
                        添加
                    </WxbButton>
                </div>

                <div className="operations-qualification-editor-actions">
                    <WxbButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={saving}
                        onClick={cancelEditPersonnelQualifications}
                    >
                        取消
                    </WxbButton>
                    <WxbButton
                        type="button"
                        variant="primary"
                        size="sm"
                        disabled={saving}
                        onClick={() => savePersonnelQualifications(personnel)}
                    >
                        {saving ? '保存中...' : '保存资质'}
                    </WxbButton>
                </div>
            </div>
        );
    }, [
        addQualificationDraft,
        cancelEditPersonnelQualifications,
        draftAddLevel,
        draftAddQualificationId,
        qualificationDrafts,
        qualificationOptions,
        qualificationSelectOptions,
        removeQualificationDraft,
        savePersonnelQualifications,
        savingQualificationEmployeeId,
        updateQualificationDraft,
    ]);

    const updateOperationRequirement = useCallback((
        position: number,
        index: number,
        patch: Partial<OperationQualificationRequirement>,
    ) => {
        setOperationPositionRequirements((current) => {
            const currentRequirements = current[position] || [];
            if (!currentRequirements[index]) return current;
            const nextRequirements = [...currentRequirements];
            nextRequirements[index] = {
                ...nextRequirements[index],
                ...patch,
            };
            return {
                ...current,
                [position]: nextRequirements,
            };
        });
    }, []);

    const addOperationRequirement = useCallback((position: number) => {
        const qualificationId = Number(newOperationRequirement.qualification_id);
        const option = operationQualificationOptions.find((qualification) => qualification.id === qualificationId);

        if (!Number.isFinite(qualificationId) || !option) {
            wxbToast.warning('请选择资质');
            return;
        }

        setOperationPositionRequirements((current) => {
            const currentRequirements = current[position] || [];
            if (currentRequirements.some((requirement) => requirement.qualification_id === qualificationId)) {
                wxbToast.warning('该位置已存在该资质要求');
                return current;
            }

            return {
                ...current,
                [position]: [
                    ...currentRequirements,
                    {
                        id: null,
                        qualification_id: qualificationId,
                        qualification_name: option.qualification_name,
                        min_level: normalizeQualificationLevel(newOperationRequirement.min_level),
                        is_mandatory: newOperationRequirement.is_mandatory ? 1 : 0,
                    },
                ],
            };
        });
        setNewOperationRequirement(DEFAULT_NEW_REQUIREMENT_DRAFT);
    }, [newOperationRequirement, operationQualificationOptions]);

    const removeOperationRequirement = useCallback((position: number, index: number) => {
        setOperationPositionRequirements((current) => ({
            ...current,
            [position]: (current[position] || []).filter((_requirement, requirementIndex) => requirementIndex !== index),
        }));
    }, []);

    const copyOperationRequirementsToNextPosition = useCallback((position: number) => {
        const nextPosition = position + 1;
        if (nextPosition > currentRequiredPeople) {
            wxbToast.info('已是最后一个位置');
            return;
        }

        setOperationPositionRequirements((current) => ({
            ...current,
            [nextPosition]: (current[position] || []).map((requirement) => ({
                ...requirement,
                id: null,
            })),
        }));
        setActiveOperationPosition(nextPosition.toString());
        wxbToast.success(`已复制 P${position} 的资质要求到 P${nextPosition}`);
    }, [currentRequiredPeople]);

    const saveOperationRequirements = useCallback(async (operationId: number, people: number) => {
        for (let position = 1; position <= people; position += 1) {
            const qualifications = (operationPositionRequirements[position] || []).map((requirement) => ({
                qualification_id: requirement.qualification_id,
                min_level: normalizeQualificationLevel(requirement.min_level),
                is_mandatory: normalizeRequirementMandatory(requirement.is_mandatory) ? 1 : 0,
            }));

            await api.put(`/operation-qualifications/${operationId}/position/${position}`, {
                qualifications,
            });
        }
    }, [operationPositionRequirements]);

    const handleSubmit = useCallback(async () => {
        const nextErrors = validateForm(formState);
        setFormErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) {
            wxbToast.warning('请先修正表单内容');
            return;
        }

        const payload = {
            operation_name: formState.operation_name.trim(),
            standard_time: Number(formState.standard_time),
            required_people: toPositiveInteger(formState.required_people, 1),
            operation_type_id: formState.operation_type_id,
            description: formState.description.trim() || null,
        };

        if (editingOperation && (operationQualificationsLoading || operationQualificationsError)) {
            wxbToast.warning(operationQualificationsLoading ? '资质要求仍在加载，请稍后保存' : '资质要求加载失败，请先重新加载');
            return;
        }

        setSubmitting(true);
        try {
            if (editingOperation) {
                await api.put(`/operations/${editingOperation.id}`, payload);
                await saveOperationRequirements(editingOperation.id, payload.required_people);
                wxbToast.success('操作和资质要求已更新');
            } else {
                await api.post('/operations', payload);
                wxbToast.success('操作创建成功');
            }

            setModalVisible(false);
            setEditingOperation(null);
            operationQualificationLoadRef.current += 1;
            fetchData();
        } catch (error: any) {
            wxbToast.error(error?.response?.data?.error || '保存操作失败');
        } finally {
            setSubmitting(false);
        }
    }, [
        editingOperation,
        fetchData,
        formState,
        operationQualificationsError,
        operationQualificationsLoading,
        saveOperationRequirements,
    ]);

    const openDeleteModal = useCallback((operation: Operation) => {
        setDeleteCandidate(operation);
        setDeleteError('');
    }, []);

    const closeDeleteModal = useCallback(() => {
        if (deleteSubmitting) return;
        setDeleteCandidate(null);
        setDeleteError('');
    }, [deleteSubmitting]);

    const handleDelete = useCallback(async () => {
        if (!deleteCandidate || bulkDeleteSubmitting) return;

        const operationId = deleteCandidate.id;
        setDeleteSubmitting(true);
        setDeleteError('');
        try {
            const response = await api.delete<{ message?: string }>(`/operations/${operationId}`);
            wxbToast.success(response.data?.message || '操作已删除');
            setDeleteCandidate(null);
            setSelectedOperationKeys((currentKeys) => currentKeys.filter((key) => Number(key) !== operationId));
            fetchData();
        } catch (err: any) {
            const message = err.response?.data?.message || err.response?.data?.error || '删除操作失败';
            setDeleteError(message);
            wxbToast.error(message);
        } finally {
            setDeleteSubmitting(false);
        }
    }, [bulkDeleteSubmitting, deleteCandidate, fetchData]);

    const handleBulkDelete = useCallback(async () => {
        if (deleteSubmitting || bulkDeleteSubmitting || selectedDeletableOperations.length === 0) return;

        const targets = selectedDeletableOperations;
        setBulkDeleteSubmitting(true);
        try {
            const results = await Promise.allSettled(
                targets.map((operation) => api.delete(`/operations/${operation.id}`)),
            );
            const deletedIds = new Set<number>();
            let failedCount = 0;

            results.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    deletedIds.add(targets[index].id);
                    return;
                }
                failedCount += 1;
            });

            if (deletedIds.size > 0) {
                setSelectedOperationKeys((currentKeys) =>
                    currentKeys.filter((key) => !deletedIds.has(Number(key)))
                );
                wxbToast.success(`已删除 ${deletedIds.size} 项操作`);
            }

            if (failedCount > 0) {
                wxbToast.error(`${failedCount} 项操作删除失败，请刷新后重试`);
            }

            fetchData();
        } finally {
            setBulkDeleteSubmitting(false);
        }
    }, [bulkDeleteSubmitting, deleteSubmitting, fetchData, selectedDeletableOperations]);

    const renderOperationRequirementPositionPanel = useCallback((position: number) => {
        const requirements = operationPositionRequirements[position] || [];
        const selectedQualificationIds = new Set(requirements.map((requirement) => requirement.qualification_id));
        const mandatoryCount = requirements.filter((requirement) => normalizeRequirementMandatory(requirement.is_mandatory)).length;
        const optionalCount = requirements.length - mandatoryCount;
        const addOptions = operationRequirementSelectOptions.map((option) => ({
            ...option,
            disabled: selectedQualificationIds.has(Number(option.value)),
        }));

        return (
            <div className="operations-operation-qualification-panel">
                <div className="operations-operation-qualification-head">
                    <span className="operations-operation-qualification-title">P{position} 资质要求</span>
                    <span className="operations-operation-qualification-metrics">
                        <WxbBadge status="error" variant="outline" code="必须" label={String(mandatoryCount)} />
                        <WxbBadge status="success" variant="outline" code="可选" label={String(optionalCount)} />
                    </span>
                    <WxbButton
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={position >= currentRequiredPeople || operationQualificationsLoading}
                        onClick={() => copyOperationRequirementsToNextPosition(position)}
                    >
                        复制到下一位置
                    </WxbButton>
                </div>

                <div className="operations-operation-qualification-add">
                    <WxbSelect
                        label="资质"
                        value={newOperationRequirement.qualification_id?.toString()}
                        placeholder="选择资质"
                        options={addOptions}
                        showSearch
                        optionFilterProp="label"
                        onChange={(value) => {
                            const qualificationId = Number(value);
                            setNewOperationRequirement((current) => ({
                                ...current,
                                qualification_id: Number.isFinite(qualificationId) ? qualificationId : null,
                            }));
                        }}
                    />
                    <WxbInputNumber
                        label="最低等级"
                        min={QUALIFICATION_LEVEL_MIN}
                        max={QUALIFICATION_LEVEL_MAX}
                        precision={0}
                        value={newOperationRequirement.min_level}
                        className="operations-operation-qualification-level"
                        onChange={(value) => setNewOperationRequirement((current) => ({
                            ...current,
                            min_level: normalizeQualificationLevel(value, current.min_level),
                        }))}
                    />
                    <div className="operations-operation-qualification-switch-field">
                        <span className="wxb-label">是否必须</span>
                        <WxbSwitch
                            size="sm"
                            checked={newOperationRequirement.is_mandatory}
                            checkedChildren="必须"
                            unCheckedChildren="可选"
                            onChange={(checked) => setNewOperationRequirement((current) => ({
                                ...current,
                                is_mandatory: checked,
                            }))}
                        />
                    </div>
                    <WxbButton
                        type="button"
                        variant="primary"
                        disabled={operationQualificationsLoading}
                        onClick={() => addOperationRequirement(position)}
                    >
                        添加
                    </WxbButton>
                </div>

                {requirements.length > 0 ? (
                    <div className="operations-operation-qualification-list" role="table" aria-label={`P${position} 资质要求`}>
                        <div className="operations-operation-qualification-row operations-operation-qualification-row--head" role="row">
                            <span role="columnheader">资质</span>
                            <span role="columnheader">最低等级</span>
                            <span role="columnheader">必须</span>
                            <span role="columnheader">操作</span>
                        </div>
                        {requirements.map((requirement, index) => {
                            const rowOptions = operationRequirementSelectOptions.map((option) => ({
                                ...option,
                                disabled: selectedQualificationIds.has(Number(option.value))
                                    && Number(option.value) !== requirement.qualification_id,
                            }));

                            return (
                                <div
                                    key={`${position}-${requirement.qualification_id}-${index}`}
                                    className="operations-operation-qualification-row"
                                    role="row"
                                >
                                    <WxbSelect
                                        value={requirement.qualification_id.toString()}
                                        options={rowOptions}
                                        showSearch
                                        optionFilterProp="label"
                                        onChange={(value) => {
                                            const qualificationId = Number(value);
                                            const option = operationQualificationOptions.find((item) => item.id === qualificationId);
                                            updateOperationRequirement(position, index, {
                                                qualification_id: qualificationId,
                                                qualification_name: option?.qualification_name || requirement.qualification_name,
                                            });
                                        }}
                                    />
                                    <WxbInputNumber
                                        min={QUALIFICATION_LEVEL_MIN}
                                        max={QUALIFICATION_LEVEL_MAX}
                                        precision={0}
                                        value={normalizeQualificationLevel(requirement.min_level)}
                                        className="operations-operation-qualification-level"
                                        onChange={(value) => updateOperationRequirement(position, index, {
                                            min_level: normalizeQualificationLevel(value, requirement.min_level),
                                        })}
                                    />
                                    <WxbSwitch
                                        size="sm"
                                        checked={normalizeRequirementMandatory(requirement.is_mandatory)}
                                        checkedChildren="必须"
                                        unCheckedChildren="可选"
                                        onChange={(checked) => updateOperationRequirement(position, index, {
                                            is_mandatory: checked ? 1 : 0,
                                        })}
                                    />
                                    <WxbIconButton
                                        icon={<OperationIcon name="delete" />}
                                        label={`删除 P${position} 的资质要求`}
                                        size="sm"
                                        variant="danger"
                                        onClick={() => removeOperationRequirement(position, index)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <WxbEmpty className="operations-operation-qualification-empty" description="当前位置暂无资质要求" />
                )}
            </div>
        );
    }, [
        addOperationRequirement,
        copyOperationRequirementsToNextPosition,
        currentRequiredPeople,
        newOperationRequirement,
        operationPositionRequirements,
        operationQualificationOptions,
        operationQualificationsLoading,
        operationRequirementSelectOptions,
        removeOperationRequirement,
        updateOperationRequirement,
    ]);

    const operationRequirementTabs = useMemo(
        () => Array.from({ length: currentRequiredPeople }, (_value, index) => {
            const position = index + 1;
            const requirements = operationPositionRequirements[position] || [];
            return {
                key: position.toString(),
                label: (
                    <span className="operations-operation-position-tab-label">
                        P{position}
                        <WxbBadge status={requirements.length > 0 ? 'info' : 'neutral'} variant="code" label={String(requirements.length)} />
                    </span>
                ),
                children: renderOperationRequirementPositionPanel(position),
            };
        }),
        [currentRequiredPeople, operationPositionRequirements, renderOperationRequirementPositionPanel],
    );

    const qualifiedDetailsTabs = useMemo(
        () => (qualifiedDetails?.positions ?? []).map((position) => ({
            key: getPositionKey(position.position_number),
            label: `P${position.position_number} (${position.qualified_count})`,
        })),
        [qualifiedDetails],
    );

    const activeQualifiedPosition = useMemo(
        () => (qualifiedDetails?.positions ?? []).find(
            (position) => getPositionKey(position.position_number) === qualifiedDetailsActivePosition,
        ) ?? qualifiedDetails?.positions[0] ?? null,
        [qualifiedDetails, qualifiedDetailsActivePosition],
    );

    const qualifiedPersonnelTableScroll = useMemo(() => ({
        x: Math.max(QUALIFIED_TABLE_MIN_SCROLL_X, qualifiedModalDimensions.width - 180),
        y: Math.max(QUALIFIED_TABLE_MIN_SCROLL_Y, qualifiedModalDimensions.height - QUALIFIED_TABLE_FIXED_HEIGHT),
    }), [qualifiedModalDimensions]);

    const qualifiedPersonnelColumns: WxbDataTableProps<QualifiedPersonnel>['columns'] = useMemo(() => [
        {
            title: '人员',
            dataIndex: 'employee_name',
            width: 180,
            render: (_value: string, record) => (
                <span className="operations-person-cell">
                    <span className="operations-person-name">{record.employee_name}</span>
                    <span className="operations-person-code">{record.employee_code}</span>
                </span>
            ),
        },
        {
            title: '组织',
            width: 180,
            render: (_value, record) => {
                const primaryUnit = record.team_name || record.department_name || record.unit_name || '-';
                const secondaryUnit = record.team_name && record.department_name
                    ? record.department_name
                    : record.unit_name && record.unit_name !== primaryUnit
                        ? record.unit_name
                        : null;

                return (
                    <span className="operations-person-cell">
                        <span className="operations-person-name">{primaryUnit}</span>
                        {secondaryUnit && <span className="operations-person-code">{secondaryUnit}</span>}
                    </span>
                );
            },
        },
        {
            title: '匹配资质',
            render: (_value, record) => {
                const requirements = sortRequirementsByLevel(activeQualifiedPosition?.requirements ?? []);
                const matchedQualifications = requirements
                    .map((requirement) => {
                        const employeeQualification = record.qualifications.find(
                            (qualification) => qualification.qualification_id === requirement.qualification_id,
                        );

                        if (!employeeQualification) return null;

                        return {
                            ...requirement,
                            qualification_level: employeeQualification.qualification_level,
                        };
                    })
                    .filter((item): item is QualifiedRequirement & { qualification_level: number } => item !== null);

                if (matchedQualifications.length === 0) {
                    return <span className="operations-muted">无必需资质</span>;
                }

                return (
                    <span className="operations-qualified-tags">
                        {matchedQualifications.map((qualification) => (
                            <WxbTag
                                key={qualification.qualification_id}
                                color={getQualificationLevelColor(qualification.qualification_level)}
                            >
                                {qualification.qualification_name} L{qualification.qualification_level}
                            </WxbTag>
                        ))}
                    </span>
                );
            },
        },
        {
            title: '操作',
            width: 100,
            render: (_value, record) => (
                <WxbButton
                    type="button"
                    variant={editingPersonnelId === record.employee_id ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => beginEditPersonnelQualifications(record)}
                >
                    {editingPersonnelId === record.employee_id ? '收起' : '调整资质'}
                </WxbButton>
            ),
        },
    ], [activeQualifiedPosition, beginEditPersonnelQualifications, editingPersonnelId]);

    const columns: WxbDataTableProps<Operation>['columns'] = useMemo(() => [
        {
            title: '操作编码',
            dataIndex: 'operation_code',
            width: 130,
            sorter: (left, right) => left.operation_code.localeCompare(right.operation_code),
            render: (value: string) => <WxbTag color="blue">{value}</WxbTag>,
        },
        {
            title: '操作名称',
            dataIndex: 'operation_name',
            width: 160,
            sorter: (left, right) => left.operation_name.localeCompare(right.operation_name),
        },
        {
            title: '标准耗时',
            dataIndex: 'standard_time',
            width: 110,
            sorter: (left, right) => toFiniteNumber(left.standard_time) - toFiniteNumber(right.standard_time),
            render: (value: number) => (
                <span className="operations-inline-metric">
                    <OperationIcon name="clock" />
                    {toFiniteNumber(value).toFixed(1)} 小时
                </span>
            ),
        },
        {
            title: '所需人数',
            dataIndex: 'required_people',
            width: 100,
            sorter: (left, right) => toFiniteNumber(left.required_people, 1) - toFiniteNumber(right.required_people, 1),
            render: (value: number) => (
                <span className="operations-inline-metric">
                    <OperationIcon name="people" />
                    {toFiniteNumber(value, 1)} 人
                </span>
            ),
        },
        {
            title: '操作类型',
            dataIndex: 'operation_type_name',
            width: 145,
            filters: operationTypes.map((type) => ({ text: type.type_name, value: type.id })),
            onFilter: (value, record) => record.operation_type_id === value,
            render: (_value: string, record) => {
                const operationType = record.operation_type_id
                    ? operationTypeById.get(record.operation_type_id)
                    : undefined;

                if (!operationType) {
                    return <WxbBadge status="neutral" variant="code" label="未分配" />;
                }

                return (
                    <span className="operations-type-cell">
                        <WxbTag color="blue">{operationType.type_name}</WxbTag>
                        <span className="operations-type-team">{operationType.team_code}</span>
                    </span>
                );
            },
        },
        {
            title: '描述',
            dataIndex: 'description',
            width: 150,
            ellipsis: true,
            render: (value: string | null) => (
                <WxbTooltip title={value || '无描述'}>
                    <span className="operations-description">{value || '-'}</span>
                </WxbTooltip>
            ),
        },
        {
            title: '合格人数',
            width: 130,
            render: (_value, record) => {
                // 合格人数只反映 1..所需人数 的有效位置。qualified-personnel 接口按 oqr 里出现的
                // 最大 position_number 返回数组，可能含超出所需人数的残留位置（历史孤儿数据：所需
                // 人数曾被调小，旧资质行未随之清理）。有效位置正常展示；残留位置不混入计数，而是
                // 单独用告警徽标显式标出，提示该操作存在脏数据、重新编辑保存即可由后端自动收口。
                const requiredPeople = toFiniteNumber(record.required_people, 1);
                const allCounts = qualifiedPersonnelMap[record.id] || [];
                const counts = allCounts.slice(0, requiredPeople);
                const orphanCount = Math.max(0, allCounts.length - requiredPeople);

                if (counts.length === 0 && orphanCount === 0) {
                    return <span className="operations-muted">-</span>;
                }

                return (
                    <span
                        className="operations-position-list operations-position-list--interactive"
                        role="button"
                        tabIndex={0}
                        aria-label={`查看 ${record.operation_name} 的合格人员明细`}
                        onDoubleClick={() => openQualifiedPersonnelDetails(record)}
                        onKeyDown={(event) => {
                            if (!isActivationKey(event)) return;
                            event.preventDefault();
                            openQualifiedPersonnelDetails(record);
                        }}
                    >
                        {counts.map((count, index) => (
                            <WxbTooltip key={`${record.id}-${index}`} title={`位置 ${index + 1}: ${count} 人合格，双击查看名单`}>
                                <span>
                                    <WxbBadge
                                        status={count > 0 ? 'success' : 'error'}
                                        variant="outline"
                                        code={`P${index + 1}`}
                                        label={String(count)}
                                    />
                                </span>
                            </WxbTooltip>
                        ))}
                        {orphanCount > 0 && (
                            <WxbTooltip
                                title={`存在超出所需人数（${requiredPeople} 人）的残留资质要求，最高至位置 ${allCounts.length}。这是历史遗留的脏数据，重新编辑并保存该操作即可自动清理。`}
                            >
                                <span>
                                    <WxbBadge
                                        status="warning"
                                        variant="outline"
                                        code="残留"
                                        label={String(orphanCount)}
                                    />
                                </span>
                            </WxbTooltip>
                        )}
                    </span>
                );
            },
        },
        {
            title: '操作',
            width: 96,
            render: (_value, record) => {
                const externalReferenceCount = getExternalReferenceCount(record);
                const deleteBlocked = externalReferenceCount > 0;
                const deleteButton = (
                    <WxbButton
                        type="button"
                        variant="danger"
                        size="sm"
                        aria-label={
                            deleteBlocked
                                ? `无法删除 ${record.operation_name}，已被引用`
                                : `删除 ${record.operation_name}`
                        }
                        disabled={deleteBlocked || bulkDeleteSubmitting}
                        onClick={() => openDeleteModal(record)}
                    >
                        <OperationIcon name="delete" />
                    </WxbButton>
                );

                return (
                    <span className="operations-table-actions">
                        <WxbTooltip title="编辑操作和资质要求">
                            <WxbButton
                                type="button"
                                variant="ghost"
                                size="sm"
                                aria-label={`编辑 ${record.operation_name} 和资质要求`}
                                onClick={() => openEditModal(record)}
                            >
                                <OperationIcon name="edit" />
                            </WxbButton>
                        </WxbTooltip>
                        <WxbTooltip
                            title={
                                bulkDeleteSubmitting
                                    ? '正在批量删除'
                                    : deleteBlocked
                                        ? getDeleteBlockedReason(record)
                                        : '删除操作'
                            }
                        >
                            <span>{deleteButton}</span>
                        </WxbTooltip>
                    </span>
                );
            },
        },
    ], [bulkDeleteSubmitting, openDeleteModal, openEditModal, openQualifiedPersonnelDetails, operationTypeById, operationTypes, qualifiedPersonnelMap]);

    const rowSelection: WxbDataTableProps<Operation>['rowSelection'] = {
        selectedRowKeys: selectedOperationKeys,
        onChange: setSelectedOperationKeys,
        columnWidth: 44,
        preserveSelectedRowKeys: true,
    };

    const bulkDeleteLabel = bulkDeleteSubmitting
        ? '删除中...'
        : selectedBlockedCount > 0
            ? `删除可删除项 (${selectedDeletableOperations.length})`
            : '批量删除';

    const bulkDeleteDescription = selectedBlockedCount > 0
        ? `将删除 ${selectedDeletableOperations.length} 项未被引用的操作；${selectedBlockedCount} 项已被引用的操作会保留。删除后无法恢复。`
        : `确定删除已选择的 ${selectedDeletableOperations.length} 项操作吗？删除后无法恢复。`;

    const deleteFooter = (
        <div className="operations-delete-footer">
            <WxbButton type="button" variant="ghost" onClick={closeDeleteModal} disabled={deleteSubmitting}>
                取消
            </WxbButton>
            <WxbButton type="button" variant="danger" onClick={handleDelete} disabled={deleteSubmitting}>
                {deleteSubmitting ? '删除中...' : '确认删除'}
            </WxbButton>
        </div>
    );

    return (
        <WxbPageShell size="full" gap="lg" className="operations-page">
            <WxbPageHeader
                eyebrow="Master Data"
                title="操作管理"
                description="维护可复用的标准操作、标准耗时、所需人数和操作类型映射，供工艺模板与排班求解复用。"
                meta={(
                    <>
                        <WxbTag color="blue">共 {operations.length} 项</WxbTag>
                        <WxbTag color="green">{operationTypes.length} 个类型</WxbTag>
                    </>
                )}
                actions={(
                    <span className="operations-header-action-group">
                        <WxbTooltip title={loading ? '刷新中' : '刷新数据'}>
                            <WxbButton
                                type="button"
                                variant="secondary"
                                className="operations-header-refresh"
                                onClick={fetchData}
                                disabled={loading}
                                aria-label={loading ? '刷新中' : '刷新数据'}
                            >
                                <OperationIcon name="refresh" />
                            </WxbButton>
                        </WxbTooltip>
                        <WxbButton type="button" variant="primary" onClick={openCreateModal}>
                            <OperationIcon name="plus" />
                            新增操作
                        </WxbButton>
                    </span>
                )}
            />

            <WxbPageSection variant="framed" density="compact" className="operations-section">
                <WxbTabs activeKey={activeTeamId} onChange={setActiveTeamId} items={tabItems} className="operations-tabs" />

                <WxbPageToolbar
                    className="operations-toolbar"
                    leading={(
                        <WxbSearchInput
                            value={searchText}
                            placeholder="搜索操作名称、编码或类型"
                            onChange={setSearchText}
                            onSearch={setSearchText}
                        />
                    )}
                    filters={(
                        <>
                            <WxbSelect
                                value={peopleFilter}
                                options={peopleOptions}
                                onChange={(value) => setPeopleFilter(String(value))}
                                className="operations-filter"
                            />
                            <WxbSelect
                                value={typeFilter}
                                options={typeOptions}
                                onChange={(value) => setTypeFilter(String(value))}
                                className="operations-filter"
                                showSearch
                                optionFilterProp="label"
                            />
                            <WxbSelect
                                value={deleteStatusFilter}
                                options={deleteStatusOptions}
                                onChange={(value) => setDeleteStatusFilter(String(value))}
                                className="operations-filter"
                            />
                        </>
                    )}
                    summary={`当前显示 ${filteredOperations.length} 项`}
                />

                <WxbBulkActionBar
                    className="operations-bulk-actions"
                    selectedCount={selectedOperations.length}
                    clearLabel="清除选择"
                    onClear={() => setSelectedOperationKeys([])}
                    summary={(
                        <span className="operations-bulk-summary">
                            已选 <strong>{selectedOperations.length}</strong> 项
                            <span>可删除 {selectedDeletableOperations.length} 项</span>
                            {selectedBlockedCount > 0 && <span>已引用 {selectedBlockedCount} 项</span>}
                        </span>
                    )}
                    actions={[
                        {
                            key: 'bulk-delete',
                            label: bulkDeleteLabel,
                            variant: 'danger',
                            disabled: bulkDeleteSubmitting || selectedDeletableOperations.length === 0,
                            onClick: handleBulkDelete,
                            confirm: {
                                title: '批量删除操作',
                                description: bulkDeleteDescription,
                                okText: '删除',
                                cancelText: '取消',
                            },
                        },
                    ]}
                />

                <WxbDataTable<Operation>
                    columns={columns}
                    dataSource={filteredOperations}
                    rowKey="id"
                    rowSelection={rowSelection}
                    loading={loading}
                    density="compact"
                    emptyState={{
                        description: '暂无匹配操作',
                        action: <WxbButton type="button" variant="secondary" onClick={openCreateModal}>新增操作</WxbButton>,
                    }}
                    errorState={loadError ? {
                        title: '操作数据加载失败',
                        description: '请检查后端服务或稍后重试。',
                        action: <WxbButton type="button" variant="secondary" onClick={fetchData}>重新加载</WxbButton>,
                    } : undefined}
                    scroll={{ x: 1110 }}
                    pagination={{
                        current: operationPage,
                        pageSize: operationPageSize,
                        showSizeChanger: true,
                        pageSizeOptions: ['20', '50', '100', '200'],
                        onChange: (page, pageSize) => {
                            setOperationPage(page);
                            setOperationPageSize(pageSize);
                        },
                        showTotal: (total) => `共 ${total} 条`,
                    }}
                />
            </WxbPageSection>

            <WxbModal
                title={editingOperation ? `编辑操作 - ${editingOperation.operation_name}` : '新增操作'}
                open={modalVisible}
                onCancel={closeModal}
                onOk={handleSubmit}
                confirmLoading={submitting}
                okText={editingOperation ? '保存操作和资质' : '创建操作'}
                cancelText="取消"
                width={editingOperation ? 980 : 560}
                destroyOnClose
            >
                <form
                    className={editingOperation ? 'operations-form operations-form--combined' : 'operations-form'}
                    onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit();
                    }}
                >
                    {editingOperation ? (
                        <div className="operations-edit-modal">
                            <div className="operations-edit-summary">
                                <span className="operations-edit-summary-main">
                                    <WxbTag color="blue">{editingOperation.operation_code}</WxbTag>
                                    <span>{editingOperation.operation_name}</span>
                                </span>
                                <span className="operations-edit-summary-meta">
                                    <WxbBadge status="info" variant="outline" code="人数" label={`${currentRequiredPeople}`} />
                                    <WxbBadge status="info" variant="outline" code="资质" label={`${visibleRequirementCount}`} />
                                </span>
                            </div>

                            <div className="operations-edit-content">
                                <section className="operations-edit-section operations-edit-section--basic" aria-labelledby="operations-basic-section-title">
                                    <div className="operations-edit-section-head">
                                        <span id="operations-basic-section-title" className="operations-edit-section-title">基础信息</span>
                                    </div>

                                    <div className="operations-edit-pane">
                                        <WxbInput label="操作编码" value={editingOperation.operation_code} disabled />

                                        <WxbInput
                                            label="操作名称"
                                            value={formState.operation_name}
                                            placeholder="如 反应袋安装"
                                            error={formErrors.operation_name}
                                            onChange={(event) => updateFormField('operation_name', event.target.value)}
                                        />

                                        <div className="operations-form-grid">
                                            <WxbInputNumber
                                                label="标准耗时（小时）"
                                                min={0.1}
                                                step={0.5}
                                                precision={1}
                                                value={formState.standard_time ?? undefined}
                                                placeholder="请输入标准耗时"
                                                error={formErrors.standard_time}
                                                onChange={(value) => updateFormField('standard_time', value === null ? null : Number(value))}
                                            />
                                            <WxbInputNumber
                                                label="所需人数"
                                                min={1}
                                                max={20}
                                                precision={0}
                                                value={formState.required_people}
                                                error={formErrors.required_people}
                                                onChange={(value) => updateFormField('required_people', Number(value ?? 1))}
                                            />
                                        </div>

                                        <WxbSelect
                                            label="操作类型"
                                            value={formState.operation_type_id?.toString() ?? 'none'}
                                            placeholder="请选择操作类型"
                                            options={formTypeOptions}
                                            showSearch
                                            optionFilterProp="label"
                                            onChange={(value) => {
                                                const nextValue = String(value);
                                                updateFormField(
                                                    'operation_type_id',
                                                    nextValue === 'none' ? null : Number(nextValue),
                                                );
                                            }}
                                        />

                                        <WxbTextarea
                                            label="描述"
                                            rows={3}
                                            value={formState.description}
                                            placeholder="请输入操作描述（可选）"
                                            onChange={(event) => updateFormField('description', event.target.value)}
                                        />
                                    </div>
                                </section>

                                <section className="operations-edit-section operations-edit-section--qualifications" aria-labelledby="operations-qualification-section-title">
                                    <div className="operations-edit-section-head">
                                        <span id="operations-qualification-section-title" className="operations-edit-section-title">资质要求</span>
                                        <span className="operations-edit-section-badges">
                                            <WxbBadge status="info" variant="outline" code="岗位" label={String(currentRequiredPeople)} />
                                            <WxbBadge status="info" variant="outline" code="资质" label={String(visibleRequirementCount)} />
                                        </span>
                                    </div>

                                    <div className="operations-edit-pane operations-operation-qualification-editor">
                                        <div className="operations-operation-qualification-summary">
                                            <span>资质要求按当前所需人数生成 P1 至 P{currentRequiredPeople}。</span>
                                        </div>

                                        {hiddenRemovedRequirementCount > 0 && (
                                            <WxbAlert variant="warning" title="保存后会清理超出人数的岗位资质">
                                                当前所需人数为 {currentRequiredPeople} 人，P{currentRequiredPeople + 1} 及以后共有 {hiddenRemovedRequirementCount} 项资质要求将不再保存。
                                            </WxbAlert>
                                        )}

                                        {operationQualificationsLoading ? (
                                            <div className="operations-operation-qualification-loading">
                                                <WxbSpinner tip="正在加载资质要求" />
                                            </div>
                                        ) : operationQualificationsError ? (
                                            <WxbEmpty
                                                description="资质要求加载失败"
                                                action={(
                                                    <WxbButton
                                                        type="button"
                                                        variant="secondary"
                                                        onClick={() => loadOperationQualificationEditor(editingOperation)}
                                                    >
                                                        重新加载
                                                    </WxbButton>
                                                )}
                                            />
                                        ) : (
                                            <WxbTabs
                                                activeKey={activeOperationPositionNumber.toString()}
                                                onChange={setActiveOperationPosition}
                                                items={operationRequirementTabs}
                                                className="operations-operation-position-tabs"
                                            />
                                        )}
                                    </div>
                                </section>
                            </div>
                        </div>
                    ) : (
                        <>
                            <WxbFormField helpText="操作编码由系统自动生成，保存后不可手动编辑。">
                                <WxbEmpty
                                    className="operations-code-preview"
                                    image={<WxbTag color="blue">{nextCode || 'OP-00000'}</WxbTag>}
                                    description="即将生成的操作编码"
                                />
                            </WxbFormField>

                            <WxbInput
                                label="操作名称"
                                value={formState.operation_name}
                                placeholder="如 反应袋安装"
                                error={formErrors.operation_name}
                                onChange={(event) => updateFormField('operation_name', event.target.value)}
                            />

                            <div className="operations-form-grid">
                                <WxbInputNumber
                                    label="标准耗时（小时）"
                                    min={0.1}
                                    step={0.5}
                                    precision={1}
                                    value={formState.standard_time ?? undefined}
                                    placeholder="请输入标准耗时"
                                    error={formErrors.standard_time}
                                    onChange={(value) => updateFormField('standard_time', value === null ? null : Number(value))}
                                />
                                <WxbInputNumber
                                    label="所需人数"
                                    min={1}
                                    max={20}
                                    precision={0}
                                    value={formState.required_people}
                                    error={formErrors.required_people}
                                    onChange={(value) => updateFormField('required_people', Number(value ?? 1))}
                                />
                            </div>

                            <WxbSelect
                                label="操作类型"
                                value={formState.operation_type_id?.toString() ?? 'none'}
                                placeholder="请选择操作类型"
                                options={formTypeOptions}
                                showSearch
                                optionFilterProp="label"
                                onChange={(value) => {
                                    const nextValue = String(value);
                                    updateFormField(
                                        'operation_type_id',
                                        nextValue === 'none' ? null : Number(nextValue),
                                    );
                                }}
                            />

                            <WxbTextarea
                                label="描述"
                                rows={3}
                                value={formState.description}
                                placeholder="请输入操作描述（可选）"
                                onChange={(event) => updateFormField('description', event.target.value)}
                            />
                        </>
                    )}
                </form>
            </WxbModal>

            <WxbModal
                title="确认删除操作"
                open={deleteCandidate !== null}
                onCancel={closeDeleteModal}
                footer={deleteFooter}
                width={440}
                destroyOnClose
            >
                {deleteCandidate && (
                    <div className="operations-delete-dialog">
                        <div className="operations-delete-target">
                            <WxbTag color="blue">{deleteCandidate.operation_code}</WxbTag>
                            <span className="operations-delete-target-name">{deleteCandidate.operation_name}</span>
                        </div>
                        <p className="operations-delete-confirm-copy">
                            确定删除该操作吗？删除后无法恢复。
                        </p>

                        {deleteError && (
                            <WxbAlert variant="error" title="未能删除操作">
                                {deleteError}
                            </WxbAlert>
                        )}
                    </div>
                )}
            </WxbModal>

            <WxbModal
                title={qualifiedDetailsOperation ? `合格人员 - ${qualifiedDetailsOperation.operation_name}` : '合格人员'}
                open={qualifiedDetailsOperation !== null}
                onCancel={closeQualifiedPersonnelDetails}
                width={qualifiedModalDimensions.width}
                className={`operations-qualified-modal${qualifiedModalResizing ? ' operations-qualified-modal--resizing' : ''}`}
                destroyOnClose
                modalRender={(modal) => (
                    <div className="operations-qualified-resizable-shell">
                        {modal}
                        <span
                            className="operations-qualified-resize-handle operations-qualified-resize-handle--right"
                            role="separator"
                            aria-label="拖拽调整弹窗宽度"
                            aria-orientation="vertical"
                            tabIndex={0}
                            onPointerDown={(event) => beginQualifiedModalResize(event, 'right')}
                            onKeyDown={(event) => handleQualifiedModalResizeKeyDown(event, 'right')}
                        />
                        <span
                            className="operations-qualified-resize-handle operations-qualified-resize-handle--bottom"
                            role="separator"
                            aria-label="拖拽调整弹窗高度"
                            aria-orientation="horizontal"
                            tabIndex={0}
                            onPointerDown={(event) => beginQualifiedModalResize(event, 'bottom')}
                            onKeyDown={(event) => handleQualifiedModalResizeKeyDown(event, 'bottom')}
                        />
                        <span
                            className="operations-qualified-resize-handle operations-qualified-resize-handle--corner"
                            role="separator"
                            aria-label="拖拽调整弹窗宽度和高度"
                            tabIndex={0}
                            onPointerDown={(event) => beginQualifiedModalResize(event, 'corner')}
                            onKeyDown={(event) => handleQualifiedModalResizeKeyDown(event, 'corner')}
                        />
                    </div>
                )}
                footer={(
                    <div className="operations-qualified-footer">
                        <WxbButton type="button" variant="secondary" onClick={closeQualifiedPersonnelDetails}>
                            关闭
                        </WxbButton>
                    </div>
                )}
            >
                {qualifiedDetailsLoading ? (
                    <div className="operations-qualified-loading">
                        <WxbSpinner tip="正在加载合格人员" />
                    </div>
                ) : qualifiedDetailsError ? (
                    <WxbEmpty
                        description="合格人员明细加载失败"
                        action={(
                            <WxbButton
                                type="button"
                                variant="secondary"
                                onClick={() => qualifiedDetailsOperation && openQualifiedPersonnelDetails(qualifiedDetailsOperation)}
                            >
                                重新加载
                            </WxbButton>
                        )}
                    />
                ) : qualifiedDetails ? (
                    <div className="operations-qualified-detail">
                        <div className="operations-qualified-summary">
                            <span className="operations-qualified-summary-main">
                                <WxbTag color="blue">{qualifiedDetails.operation_code}</WxbTag>
                                <WxbBadge
                                    status="info"
                                    variant="outline"
                                    code="位置"
                                    label={`${qualifiedDetails.required_people}`}
                                />
                                <span className="operations-muted">{qualifiedDetails.operation_name}</span>
                            </span>
                        </div>

                        <WxbTabs
                            activeKey={qualifiedDetailsActivePosition}
                            onChange={setQualifiedDetailsActivePosition}
                            items={qualifiedDetailsTabs}
                            className="operations-qualified-tabs"
                        />

                        {activeQualifiedPosition && (
                            <div className="operations-qualified-panel">
                                <div className="operations-qualified-position-card">
                                    <div className="operations-qualified-position-meta">
                                        <WxbBadge
                                            status={activeQualifiedPosition.qualified_count > 0 ? 'success' : 'error'}
                                            variant="outline"
                                            code={`P${activeQualifiedPosition.position_number}`}
                                            label={`${activeQualifiedPosition.qualified_count} 人`}
                                        />
                                    </div>
                                    <div className="operations-qualified-requirement-block">
                                        <span className="operations-qualified-requirement-title">资质需求</span>
                                        <span className="operations-qualified-requirement-list">
                                            {activeQualifiedPosition.requirements.length > 0 ? (
                                                sortRequirementsByLevel(activeQualifiedPosition.requirements).map((requirement) => (
                                                    <WxbTooltip
                                                        key={`${requirement.qualification_id}-${requirement.min_level}-${String(requirement.is_mandatory)}`}
                                                        title={getRequirementLabel(requirement)}
                                                    >
                                                        <WxbTag
                                                            color={getQualificationLevelColor(requirement.min_level)}
                                                            className="operations-qualified-requirement-tag"
                                                        >
                                                            {getRequirementLabel(requirement)}
                                                        </WxbTag>
                                                    </WxbTooltip>
                                                ))
                                            ) : (
                                                <span className="operations-muted">暂无资质要求</span>
                                            )}
                                        </span>
                                    </div>
                                </div>

                                <WxbDataTable<QualifiedPersonnel>
                                    key={`qualified-personnel-${qualifiedDetails.operation_id}-${qualifiedDetailsActivePosition}-${editingPersonnelId ?? 'closed'}`}
                                    columns={qualifiedPersonnelColumns}
                                    dataSource={activeQualifiedPosition.personnel}
                                    rowKey="employee_id"
                                    density="compact"
                                    pagination={false}
                                    emptyState={{ description: '暂无合格人员' }}
                                    scroll={qualifiedPersonnelTableScroll}
                                    expandable={{
                                        expandedRowKeys: editingPersonnelId ? [editingPersonnelId] : [],
                                        expandedRowRender: (record) =>
                                            editingPersonnelId === record.employee_id
                                                ? renderPersonnelQualificationEditor(record)
                                                : null,
                                        rowExpandable: () => true,
                                        showExpandColumn: false,
                                    }}
                                />
                            </div>
                        )}
                    </div>
                ) : null}
            </WxbModal>

        </WxbPageShell>
    );
};

export default OperationsPage;
